import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { pb } from '../../lib/pb';
import '../../styles/admin.css';
import { 
  BarChart3, Box, Image, MessageSquare, Calendar, 
  PlusCircle, UserPlus, FileText, ChevronRight, 
  Clock, ArrowUpRight, LayoutDashboard, Settings
} from 'lucide-react';

export default function Dashboard() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [counts, setCounts] = useState({
    categories: 0, products: 0, projects: 0, events: 0, messages: 0,
  });
  const [latest, setLatest] = useState({
    products: [], projects: [], events: [], messages: [],
  });

  const [userName, setUserName] = useState('');

  useEffect(() => {
    setUserName(pb.authStore.model?.name || pb.authStore.model?.username || 'Admin');
    let alive = true;
    async function load() {
      setLoading(true); setError('');
      try {
        const [cat, prod, proj, evt, msg] = await Promise.all([
          pb.collection('categories').getList(1, 1).catch(() => ({ totalItems: 0 })),
          pb.collection('products').getList(1, 1).catch(() => ({ totalItems: 0 })),
          pb.collection('projects').getList(1, 1).catch(() => ({ totalItems: 0 })),
          pb.collection('events').getList(1, 1).catch(() => ({ totalItems: 0 })),
          pb.collection('messages').getList(1, 1).catch(() => ({ totalItems: 0 })),
        ]);
        const [latestProd, latestProj, latestEvt, latestMsg] = await Promise.all([
          pb.collection('products').getList(1, 5, { sort: '-created' }).catch(() => ({ items: [] })),
          pb.collection('projects').getList(1, 5, { sort: '-created' }).catch(() => ({ items: [] })),
          pb.collection('events').getList(1, 5, { sort: '-start_at,-created' }).catch(() => ({ items: [] })),
          pb.collection('messages').getList(1, 5, { sort: '-created' }).catch(() => ({ items: [] })),
        ]);
        if (!alive) return;
        setCounts({
          categories: cat.totalItems ?? 0,
          products: prod.totalItems ?? 0,
          projects: proj.totalItems ?? 0,
          events: evt.totalItems ?? 0,
          messages: msg.totalItems ?? 0,
        });
        setLatest({
          products: latestProd.items || [],
          projects: latestProj.items || [],
          events: latestEvt.items || [],
          messages: latestMsg.items || [],
        });
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, []);

  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="dash-container">
      {/* HEADER SECTION */}
      <div className="dash-welcome">
        <div className="dash-welcome-text">
          <h1>Bonjour, {userName} 👋</h1>
          <p className="muted">Voici l'état de votre activité pour ce {today}.</p>
        </div>
        <div className="dash-welcome-actions hide-sm">
          <NavLink to="/admin/planning" className="btn filled"><Calendar size={18}/> Voir le Planning</NavLink>
        </div>
      </div>

      {error && <div className="admin-error-box">{error}</div>}

      {/* STATS SECTION - Swipeable on mobile */}
      <div className="dash-stats-grid">
        <StatCard title="Produits" value={counts.products} icon={<Box size={24}/>} color="var(--admin-primary)" link="/admin/products" />
        <StatCard title="Réalisations" value={counts.projects} icon={<Image size={24}/>} color="#0ea5e9" link="/admin/projects" />
        <StatCard title="Événements" value={counts.events} icon={<Calendar size={24}/>} color="#8b5cf6" link="/admin/events" />
        <StatCard title="Messages" value={counts.messages} icon={<MessageSquare size={24}/>} color="#f43f5e" link="/admin/messages" />
      </div>

      {/* QUICK ACTIONS - Native App Style */}
      <section className="dash-section">
        <h3 className="dash-section-title">Actions Rapides</h3>
        <div className="dash-quick-grid">
          <QuickAction icon={<PlusCircle/>} label="Nouveau Projet" link="/admin/projects" color="#0ea5e9" />
          <QuickAction icon={<UserPlus/>} label="Ajouter Profil" link="/admin/team" color="#f59e0b" />
          <QuickAction icon={<FileText/>} label="Gérer Produits" link="/admin/products" color="#10b981" />
          <QuickAction icon={<Settings/>} label="Paramètres" link="/admin/settings" color="#64748b" />
        </div>
      </section>
      
      <div className="dash-layout-grid">
        {/* LATEST PROJECTS FEED */}
        <div className="dash-panel">
          <div className="dash-panel-header">
            <h3>Derniers Chantiers</h3>
            <NavLink to="/admin/projects" className="dash-panel-link">Tout voir</NavLink>
          </div>
          <div className="dash-panel-list">
            {latest.projects.map(p => (
              <ProjectRow key={p.id} item={p} />
            ))}
            {latest.projects.length === 0 && <p className="muted pad">Aucun chantier récent.</p>}
          </div>
        </div>

        {/* LATEST MESSAGES */}
        <div className="dash-panel">
          <div className="dash-panel-header">
            <h3>Nouveaux Messages</h3>
            <NavLink to="/admin/messages" className="dash-panel-link">Voir Inbox</NavLink>
          </div>
          <div className="dash-panel-list">
            {latest.messages.map(m => (
              <MessageRow key={m.id} item={m} />
            ))}
            {latest.messages.length === 0 && <p className="muted pad">Aucun message pour le moment.</p>}
          </div>
        </div>
      </div>

      <style>{`
        .dash-container { animation: fadeIn 0.4s ease; }
        .dash-welcome { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 30px; }
        .dash-welcome h1 { font-size: 28px; font-weight: 900; margin: 0 0 5px 0; color: #0f172a; }
        
        /* STATS GRID */
        .dash-stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 20px; margin-bottom: 35px; }
        
        .stat-card { 
          background: #fff; padding: 24px; border-radius: 24px; border: 1px solid var(--admin-border);
          box-shadow: 0 10px 15px -3px rgba(0,0,0,0.02); position: relative; overflow: hidden; transition: all 0.2s;
          display: flex; flex-direction: column; gap: 15px; text-decoration: none !important; color: inherit !important;
        }
        .stat-card:hover { transform: translateY(-4px); box-shadow: 0 20px 25px -5px rgba(0,0,0,0.08); }
        .stat-icon-wrap { width: 48px; height: 48px; border-radius: 14px; display: flex; align-items: center; justify-content: center; }
        .stat-info { display: flex; flex-direction: column; }
        .stat-title { font-size: 14px; font-weight: 700; color: #64748b; margin-bottom: 4px; }
        .stat-value { font-size: 32px; font-weight: 900; color: #0f172a; }

        /* QUICK ACTIONS */
        .dash-section { margin-bottom: 35px; }
        .dash-section-title { font-size: 16px; font-weight: 800; color: #0f172a; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 15px; }
        .dash-quick-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 15px; }
        
        .quick-action-btn { 
          background: #fff; border: 1px solid var(--admin-border); border-radius: 20px; padding: 20px;
          display: flex; flex-direction: column; align-items: center; gap: 10px; text-align: center;
          text-decoration: none !important; color: #0f172a !important; transition: all 0.2s;
        }
        .quick-action-btn:hover { background: #f8fafc; border-color: var(--admin-primary); transform: scale(1.05); }
        .quick-icon { width: 42px; height: 42px; border-radius: 50%; background: #f1f5f9; display: flex; align-items: center; justify-content: center; }

        /* LAYOUT GRID */
        .dash-layout-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(400px, 1fr)); gap: 20px; }
        .dash-panel { background: #fff; border: 1px solid var(--admin-border); border-radius: 24px; overflow: hidden; }
        .dash-panel-header { padding: 20px 24px; border-bottom: 1px solid var(--admin-border); display: flex; justify-content: space-between; align-items: center; }
        .dash-panel-header h3 { font-size: 18px; font-weight: 900; margin: 0; }
        .dash-panel-link { font-size: 13px; font-weight: 700; color: var(--admin-primary); text-decoration: none; }
        .dash-panel-list { display: flex; flex-direction: column; }
        
        /* ROWS */
        .feed-row { display: flex; align-items: center; gap: 15px; padding: 16px 24px; border-bottom: 1px solid #f8fafc; transition: background 0.2s; text-decoration: none !important; color: inherit !important; }
        .feed-row:hover { background: #f8fafc; }
        .feed-row:last-child { border-bottom: none; }
        .feed-avatar { width: 40px; height: 40px; border-radius: 10px; background: #f1f5f9; display: flex; align-items: center; justify-content: center; color: var(--admin-primary); }
        .feed-meta { flex: 1; }
        .feed-title { font-weight: 700; color: #0f172a; display: block; }
        .feed-sub { font-size: 13px; color: #64748b; }

        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

        @media (max-width: 900px) {
          .dash-welcome { flex-direction: column; align-items: flex-start; gap: 12px; margin-bottom: 20px; }
          .dash-welcome h1 { font-size: 22px; }
          
          .dash-container { max-width: 100%; overflow-x: hidden; }
          .dash-stats-grid { 
            display: flex; 
            overflow-x: auto; 
            gap: 12px; 
            margin: 0 0 25px 0;
            padding: 5px 0 20px 0;
            -webkit-overflow-scrolling: touch;
            touch-action: pan-x;
          }
          .dash-stats-grid::-webkit-scrollbar { height: 4px; display: block; }
          .dash-stats-grid::-webkit-scrollbar-thumb { background: var(--admin-primary); border-radius: 10px; }
          
          .stat-card { 
            min-width: 150px; 
            flex: 0 0 150px; 
            padding: 16px; 
            border-radius: 20px;
          }
          .stat-value { font-size: 22px; }
          
          .dash-layout-grid { grid-template-columns: 1fr; gap: 20px; }
          .dash-panel { max-width: 100%; min-width: 0; overflow: hidden; border: none; background: transparent; }
          .dash-panel-header { padding: 0 5px 12px 0; border: none; }
          .dash-panel-link { font-size: 12px !important; opacity: 0.8; }
          
          .dash-panel-list { 
            display: flex; 
            flex-direction: row; 
            padding: 5px 0 25px 0;
            overflow-x: auto; 
            gap: 12px;
            -webkit-overflow-scrolling: touch;
            touch-action: pan-x;
          }
          .dash-panel-list::-webkit-scrollbar { height: 5px; display: block; }
          .dash-panel-list::-webkit-scrollbar-thumb { background: var(--admin-primary); border-radius: 10px; }
          
          .feed-row { 
            flex: 0 0 260px;
            min-width: 260px; 
            flex-direction: column; 
            align-items: flex-start !important; 
            background: #fff;
            border: 1px solid var(--admin-border);
            border-radius: 22px;
            padding: 18px;
            text-decoration: none !important;
            box-shadow: 0 10px 15px -3px rgba(0,0,0,0.04);
            white-space: normal;
          }
          .feed-meta { width: 100%; border-bottom: 1px solid #f8fafc; padding-bottom: 10px; margin-bottom: 5px; }
          
          .dash-quick-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
          .quick-action-btn { padding: 15px; border-radius: 18px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02); }
          .dash-section-title { font-size: 13px; margin-bottom: 12px; }
        }
      `}</style>
    </div>
  );
}

function StatCard({ title, value, icon, color, link }) {
  return (
    <NavLink to={link} className="stat-card">
      <div className="stat-icon-wrap" style={{ background: `${color}15`, color }}>{icon}</div>
      <div className="stat-info">
        <div className="stat-title">{title}</div>
        <div className="stat-value">{value}</div>
      </div>
      <div style={{ position: 'absolute', bottom: -10, right: -10, opacity: 0.05, transform: 'scale(2)' }}>{icon}</div>
    </NavLink>
  );
}

function QuickAction({ icon, label, link, color }) {
  return (
    <NavLink to={link} className="quick-action-btn">
      <div className="quick-icon" style={{ color: color }}>{icon}</div>
      <span style={{ fontSize: 13, fontWeight: 700 }}>{label}</span>
    </NavLink>
  );
}

function ProjectRow({ item }) {
  const d = new Date(item.created).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  return (
    <NavLink to="/admin/projects" className="feed-row">
      <div className="feed-avatar"><Image size={18}/></div>
      <div className="feed-meta">
        <span className="feed-title">{item.title}</span>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
           <span className="feed-sub">{item.location || 'Localisation...'}</span>
           <span style={{ fontSize: 11, color: '#94a3b8' }}>• {d}</span>
        </div>
      </div>
      <span className="badge ok" style={{ fontSize: 10 }}>Chantier</span>
      <ChevronRight size={16} className="muted"/>
    </NavLink>
  );
}

function MessageRow({ item }) {
  const d = new Date(item.created).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  return (
    <NavLink to="/admin/messages" className="feed-row">
      <div className="feed-avatar" style={{ color: '#f43f5e', background: '#fff1f2' }}><MessageSquare size={18}/></div>
      <div className="feed-meta">
        <span className="feed-title">{item.subject || 'Message de ' + item.name}</span>
        <span className="feed-sub">{item.email}</span>
      </div>
      <span style={{ fontSize: 11, color: '#94a3b8', marginRight: 10 }}>{d}</span>
      <div className="hide-sm"><ArrowUpRight size={16} className="muted"/></div>
    </NavLink>
  );
}
